import dayjs, { extend } from 'dayjs';
import durationPlugin from 'dayjs/plugin/duration.js';
import relativeTimePlugin from 'dayjs/plugin/relativeTime.js';
import isTodayPlugin from 'dayjs/plugin/isToday.js';
import calendarPlugin from 'dayjs/plugin/calendar.js';
import utcPlugin from 'dayjs/plugin/utc.js';
import timezonePlugin from 'dayjs/plugin/timezone.js';
import isBetweenPlugin from 'dayjs/plugin/isBetween.js';
import 'dayjs/locale/nb.js';
import 'dayjs/locale/sv.js';
import { Provider } from '@vgtv/api-client';
import type { DateISO, UnixTimestamp } from '@vgtv/api-client';

import { config } from '../config.js';
import { toDayjs } from '../helpers/date.js';

extend(durationPlugin);
extend(relativeTimePlugin);
extend(calendarPlugin);
extend(isTodayPlugin);
extend(utcPlugin);
extend(timezonePlugin);
extend(isBetweenPlugin);

dayjs.tz.setDefault('Europe/Oslo');

export enum Locale {
  NORWEGIAN = 'nb',
  SWEDISH = 'sv',
}

const DEFAULT_LOCALE =
  config.provider === Provider.VGTV ? Locale.NORWEGIAN : Locale.SWEDISH;

function getCalendarDateTimeLocale(locale: Locale) {
  return {
    sameDay: '[i dag] HH:mm',
    nextDay:
      locale === Locale.NORWEGIAN ? '[i morgen] HH:mm' : '[i morgon] HH:mm',
    nextWeek: 'dddd HH:mm',
    sameElse: `DD.MM HH:mm`,
  };
}

function getCalendarDateLocale(locale: Locale) {
  return {
    sameDay: '[i dag]',
    nextDay: locale === Locale.NORWEGIAN ? '[i morgen]' : '[i morgon]',
    nextWeek: 'dddd',
    sameElse: `DD.MM`,
  };
}

/**
 * Formats unix timestamp into ISO8601 date format "YYYY-MM-DDTHH:mm:ss"
 * Ex: 2021-02-11T09:21:32.000Z
 */
function formatTimestampToIsoString(timestamp: UnixTimestamp): DateISO {
  return dayjs.unix(timestamp).toISOString();
}

/**
 * Formats duration in milliseconds into ISO8601 duration format: PnYnMnDTnHnMnS
 * Ex: 1408170 PT23M28S
 */
function formatDurationToIsoString(milliseconds: number = 0): DateISO {
  const duration = dayjs.duration(milliseconds);
  const durationWithoutMilliseconds = milliseconds - duration.milliseconds();
  return dayjs.duration(durationWithoutMilliseconds).toISOString();
}

/**
 * Formats duration into format "[hh:]mm:ss", pads seconds with zeroes, optionally pads minutes
 * Ex: 3:20, 15:01
 */
function formatDuration(milliseconds: number, padMinutes?: boolean): string {
  const duration = dayjs.duration(milliseconds);

  const hours = duration.hours();
  const minutes = duration.minutes();
  const seconds = duration.seconds();

  const shouldPadMinutes = padMinutes || hours > 0;

  return `${hours ? `${hours}:` : ''}${
    shouldPadMinutes ? pad(minutes) : minutes
  }:${pad(seconds)}`;
}

function formatDurationFromSeconds(seconds: number, padMinutes?: boolean) {
  const milliseconds = seconds * 1000;

  return formatDuration(milliseconds, padMinutes);
}

function formatDurationLocalized(ms: number): string {
  if (ms < 180000) {
    return `${Math.round(ms / 1000)} sek`;
  }
  return `${Math.round(ms / 60000)} min`;
}

/**
 * Formats unix timestamp into standard date format "dd.mm.yy"
 * Ex: 04.02.20
 */
function formatDateStandard(seconds: UnixTimestamp): string {
  return dayjs.unix(seconds).format('DD.MM.YY');
}

/**
 * @private
 *
 * Prepends number with 0 if it's a single digit
 * @param {Number} num
 * @return {String}
 */
function pad(num: number) {
  return num <= 9 ? `0${num}` : String(num);
}

/**
 * Formats unix timestamp into a human-readable date format
 * @example
 * '2 days ago'
 * 'an hour ago'
 * 'a day ago'
 * '2 years ago'
 * @see {@link https://day.js.org/docs/en/display/from-now|day.js documentation} for details
 */
function formatDateHumanReadable(
  seconds: UnixTimestamp,
  locale: Locale = DEFAULT_LOCALE
) {
  return dayjs.unix(seconds).locale(locale).fromNow();
}

/**
 * Formats unix timestamp into a human-readable calendar time
 * @example
 * 'Today at 2:30'
 * 'Tomorrow at 11:00'
 */
function formatCalendarTime(
  seconds: UnixTimestamp,
  relativeTo?: UnixTimestamp,
  locale: Locale = DEFAULT_LOCALE
) {
  const calendarTimeLocale = getCalendarDateTimeLocale(locale);
  return (
    dayjs
      .unix(seconds)
      .locale(locale)
      // calendar displays time relative to the given reference time, `null` here
      .calendar(relativeTo ? dayjs.unix(relativeTo) : null, calendarTimeLocale)
  );
}

/**
 * Formats unix timestamp into a human-readable calendar time up to 23 hours ago, then switch to standard date
 * @example
 * 'an hour ago'
 * '23 hours ago'
 * '05.03.21'
 */
function formatDateAlternate(
  seconds: UnixTimestamp,
  locale: Locale = DEFAULT_LOCALE
) {
  const daysDiff = getDaysDiff(Date.now(), seconds * 1000);

  if (daysDiff > 0) {
    return formatDateStandard(seconds);
  } else {
    return formatDateHumanReadable(seconds, locale);
  }
}

function formatIsoDateTime(seconds: UnixTimestamp) {
  return toDayjs(seconds).toISOString();
}

/**
 * Returns the absolute number of days which separate two given timestamps.
 *
 * @param {number} from - milliseconds
 * @param {number} to - milliseconds
 */
function getDaysDiff(from: number, to: number): number {
  const dayMilliseconds = 24 * 60 * 60 * 1000;
  const millisecondsDiff = Math.abs(from - to);
  return Math.round(millisecondsDiff / dayMilliseconds);
}

/**
 * Transforms a {@link DateISO|Date|number} to {@link UnixTimestamp}.
 */
function getUnixTimestamp(date: DateISO | Date | number): UnixTimestamp {
  return new Date(date).getTime() / 1000;
}

/**
 *
 * @param {DateISO} date
 * @param {string} locale
 */
function formatCalendarDate(
  date: DateISO,
  locale: Locale = DEFAULT_LOCALE
): string {
  const calendarTimeLocale = getCalendarDateLocale(locale);

  return dayjs(date).locale(locale).calendar(null, calendarTimeLocale);
}

export {
  formatDurationLocalized,
  formatDuration,
  formatDateStandard,
  formatDurationFromSeconds,
  formatDateHumanReadable,
  formatCalendarTime,
  formatCalendarDate,
  formatDateAlternate,
  formatIsoDateTime,
  formatTimestampToIsoString,
  formatDurationToIsoString,
  getDaysDiff,
  getUnixTimestamp,
};
